import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { ChevronRight, ClipboardList, ReceiptText, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSupportedLocale, type SupportedLocale } from '@nokowazzi/i18n';
import {
  Button,
  Divider,
  EmptyState,
  ListItem,
  SegmentedControl,
  Skeleton,
  toast,
  UserProfileHeader,
} from '@nokowazzi/ui';
import { useProfile, type MeProfile } from '../features/auth/useProfile';
import { countryFlag, statNumber, trustLevelLabelKey } from '../features/my/profile';
import { useUpdateLocale } from '../features/my/useUpdateLocale';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/auth';

export const Route = createFileRoute('/_tabs/my')({
  component: MyScreen,
});

function MyScreen() {
  const { t } = useTranslation();
  const session = useAuthStore((state) => state.session);
  const initializing = useAuthStore((state) => state.initializing);

  return (
    <div className="pt-[calc(var(--safe-top)+16px)]">
      <h1 className="px-5 text-title1 text-fg">{t('my.title')}</h1>
      {initializing ? <ProfileSkeleton /> : session ? <SignedInView /> : <SignedOutView />}
    </div>
  );
}

function SignedOutView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <EmptyState
      className="mt-16"
      icon={<UserRound size={48} strokeWidth={1.5} />}
      title={t('errors.unauthorized')}
      description={t('my.signInPromptDescription')}
      action={
        <Button
          onClick={() => void navigate({ to: '/auth/signin', search: { redirect: '/my' } })}
        >
          {t('actions.signIn')}
        </Button>
      }
    />
  );
}

function SignedInView() {
  const { t } = useTranslation();
  const profileQuery = useProfile();

  if (profileQuery.isPending) {
    return <ProfileSkeleton />;
  }

  if (profileQuery.isError || profileQuery.data === undefined) {
    return (
      <EmptyState
        className="mt-16"
        title={t('my.profileLoadFailed')}
        description={t('errors.generic')}
        action={
          <Button variant="tertiary" onClick={() => void profileQuery.refetch()}>
            {t('actions.retry')}
          </Button>
        }
      />
    );
  }

  return <ProfileContent profile={profileQuery.data} />;
}

function ProfileContent({ profile }: { profile: MeProfile }) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateLocale = useUpdateLocale();
  const [signingOut, setSigningOut] = useState(false);

  const avatarUrl = profile.avatar_path
    ? supabase.storage.from('avatars').getPublicUrl(profile.avatar_path).data.publicUrl
    : undefined;

  const currentLocale: SupportedLocale = isSupportedLocale(i18n.language)
    ? i18n.language
    : 'ko';

  const handleLocaleChange = (locale: SupportedLocale) => {
    if (locale === currentLocale) return;
    void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
    updateLocale.mutate(locale, {
      onError: () => toast.show({ type: 'error', message: t('errors.generic') }),
    });
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    const { error } = await supabase.auth.signOut();
    setSigningOut(false);
    if (error) {
      toast.show({ type: 'error', message: t('errors.generic') });
      return;
    }
    queryClient.clear();
  };

  return (
    <div className="flex flex-col gap-5 pb-6">
      <section className="px-5 pt-5">
        <UserProfileHeader
          profile={{
            displayName: profile.display_name,
            handle: profile.handle,
            avatarUrl,
            countryFlag: countryFlag(profile.country),
            bio: profile.bio ?? undefined,
            altitude: profile.trust_score,
            levelLabel: t(trustLevelLabelKey(profile.trust_level)),
            statsItems: [
              {
                label: t('my.stats.sales'),
                value: String(statNumber(profile.stats, 'completed_sales')),
              },
              {
                label: t('my.stats.purchases'),
                value: String(statNumber(profile.stats, 'completed_purchases')),
              },
              {
                label: t('my.stats.reviews'),
                value: String(statNumber(profile.stats, 'reviews_count')),
              },
            ],
          }}
          meterAriaLabel={t('trust.meterLabel')}
        />
      </section>

      <Divider />

      <section>
        <ListItem
          leading={<ClipboardList className="size-5 text-fg-secondary" aria-hidden="true" />}
          title={t('my.myRequests')}
          trailing={<ChevronRight className="size-5" aria-hidden="true" />}
          onClick={() => {
            // 소싱 피드의 mine 필터는 임의 search라 typed navigate 대신 history 이동
            router.history.push('/sourcing?mine=1');
          }}
        />
        <ListItem
          leading={<ReceiptText className="size-5 text-fg-secondary" aria-hidden="true" />}
          title={t('my.orders')}
          trailing={<ChevronRight className="size-5" aria-hidden="true" />}
          onClick={() => void navigate({ to: '/orders' })}
        />
      </section>

      <Divider />

      <section className="flex flex-col gap-4 px-5">
        <h2 className="text-title3 text-fg">{t('my.settings')}</h2>
        <div className="flex flex-col gap-1.5">
          <span className="text-caption1 text-fg-secondary">{t('my.language')}</span>
          <SegmentedControl<SupportedLocale>
            options={[
              { value: 'ko', label: t('my.languageKo') },
              { value: 'ja', label: t('my.languageJa') },
            ]}
            value={currentLocale}
            onChange={handleLocaleChange}
            aria-label={t('my.language')}
          />
        </div>
        <Button
          variant="tertiary"
          fullWidth
          loading={signingOut}
          onClick={() => void handleSignOut()}
        >
          {t('actions.signOut')}
        </Button>
      </section>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div aria-busy="true" className="flex flex-col gap-4 px-5 pt-5">
      <div className="flex items-center gap-4">
        <Skeleton variant="circle" className="size-20" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton variant="text" className="w-32" />
          <Skeleton variant="text" className="w-24" />
        </div>
      </div>
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}
